<template>
  <div>
    <PageHeader name="Browse Community">
      <div>
        <SearchInput v-model="searchInput" />
        <div
          class="tw-mt-2 tw-flex tw-items-center tw-justify-between md:tw-hidden"
        >
          <BaseButton
            @click="toggleFiltersSection"
            color="outline-gray"
            size="small"
          >
            <BaseIcon name="sort-tool-2" class="tw-mr-2" />
            {{ isFiltersSectionShown ? 'Hide' : 'Show' }} filters
            <template v-if="activeFiltersCount > 0">
              <span class="tw-ml-2">•</span>
              <span class="tw-ml-2">
                {{ activeFiltersCount }}
              </span>
            </template>
          </BaseButton>
          <ClearFiltersButton
            v-if="isAnyFilterActive"
            @click="handleClearFiltersButtonClick"
            is-outline
            is-rounded
          />
        </div>
        <BaseAccordionAnimation
          :is-open="isFiltersSectionShown || screenWidth > tailwindScreens.md"
        >
          <div
            class="tw-mt-4 tw-w-full tw-flex-wrap tw-items-center tw-overflow-hidden md:tw-flex"
          >
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="looking_fors"
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
              test_id="looking-for-filter"
            >
              Networking Objectives
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="roles"
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Role
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="industry_specialties"
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              has-groups
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Industry
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="living_in_country"
              searchable
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Country
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="languages"
              searchable
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Language
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="skills"
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Skills
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="challenges"
              searchable
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Challenges
            </FiltersButton>
            <FiltersButton
              class="tw-mb-4 tw-mr-4"
              state-key="interests"
              searchable
              :is-dropdown-disabled="
                isCommunityPageBlocked || !hasAccessToFilteringCommunityMembers
              "
              @click="handleFiltersClick"
              @change="applyFilters"
            >
              Interests
            </FiltersButton>
            <div v-show="isAnyFilterActive">
              <ClearFiltersButton
                @click="handleClearFiltersButtonClick"
                class="tw-mb-4 tw-mr-4"
              />
            </div>
          </div>
        </BaseAccordionAnimation>
      </div>
    </PageHeader>
    <Container>
      <div v-if="isError" class="tw-mx-auto tw-mt-16 tw-w-max">
        <Alert type="error">
          <template #title>Error occurred</template>
        </Alert>
      </div>
      <transition v-else name="fade" mode="out-in" duration="100">
        <LoadingCircle
          class="tw-mx-auto tw-mt-8"
          v-if="!mentees.length && isLoading"
        />
        <div class="tw-pt-6" v-else>
          <Card
            v-if="mentees.length"
            padding-class="tw-p-5"
            class="tw-relative"
          >
            <CommunityMenteesBlockingCover v-if="isCommunityPageBlocked" />
            <div class="tw-overflow-x-auto">
              <table class="tw-w-full">
                <thead
                  class="tw-text-xs tw-font-semibold tw-uppercase tw-text-neutral-500"
                >
                  <tr>
                    <td class="tw-pb-3 tw-pr-3">Name</td>
                    <td class="tw-pb-3 tw-pr-3">Networking objectives</td>
                    <td class="tw-pb-3 tw-pr-3">Location</td>
                    <td class="tw-pb-3 tw-pr-3">Industries</td>
                  </tr>
                </thead>
                <tbody>
                  <CommunityMentee
                    v-for="mentee in mentees"
                    :key="mentee.id"
                    :mentee="mentee"
                    :is-disabled="isCommunityPageBlocked"
                  />
                </tbody>
              </table>
            </div>
          </Card>
          <div
            v-else
            class="tw-rounded-md tw-bg-neutral-100 tw-p-6 tw-text-center"
          >
            <h2 class="tw-heading-2 tw-text-neutral-500">
              Found no mentees matching your criteria. Try to change filters.
            </h2>
            <BaseButton
              @click="clearFilters"
              color="outline-gray"
              class="tw-mt-4"
            >
              <CloseIcon class="tw-mr-2 tw-h-3 tw-w-3" />
              Clear all filters
            </BaseButton>
          </div>
          <div
            v-if="!isCommunityPageBlocked && canLoadMoreMentees"
            class="tw-mt-8 tw-text-center"
          >
            <BaseButton
              color="outline-gray"
              @click="loadMentees"
              :is-disabled="isLoading"
            >
              {{ isLoading ? 'Loading more members...' : 'Load more members' }}
            </BaseButton>
          </div>
        </div>
      </transition>
    </Container>

    <LightPlanAccessDeniedModal
      v-if="isSubscriptionGroup(SUBSCRIPTION_GROUP.LIGHT)"
      :name="lightPlanAccessDeniedModalName"
      segment-prompt-type="community"
    >
      <template #header>Advanced Networking is a Pro feature</template>
      <template #description>
        With the Pro plan you can use the advanced filters and connect with
        other community members.
      </template>
    </LightPlanAccessDeniedModal>
  </div>
</template>

<script setup>
import PageHeader from '@/shared/components/PageHeader.vue'
import Container from '@/shared/components/Container.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import FiltersButton from '@/shared/components/filters/FiltersButton.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import ClearFiltersButton from '@/shared/components/filters/ClearFiltersButton.vue'
import Alert from '@/shared/components/Alert.vue'
import Card from '@/shared/components/Card.vue'
import CommunityMentee from '@/modules/growthmentor/community/components/CommunityMentee.vue'
import CommunityMenteesBlockingCover from '@/modules/growthmentor/community/components/CommunityMenteesBlockingCover.vue'
import LightPlanAccessDeniedModal from '@/shared/components/modals/LightPlanAccessDeniedModal.vue'
import CloseIcon from '@/assets/images/icons/close.svg'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useFiltersProvider } from '@/shared/composables/filters/useFiltersProvider.js'
import _debounce from 'lodash/debounce.js'
import {
  ACCESS_TYPE,
  PROFILE_STATUS,
  PROFILE_VISIBILITY,
  ROLE,
  SUBSCRIPTION_GROUP,
} from '@/shared/data/permissionSystem.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { queryParameters } from '@/shared/utils/queryParameters.js'
import axios from 'axios'
import { useModals } from '@/shared/composables/useModals.js'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import BaseAccordionAnimation from '@/shared/components/BaseAccordionAnimation.vue'
import { useScreenWidth } from '@/shared/composables/useScreenWidth.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { router } from '@inertiajs/vue3'
import SearchInput from '@/shared/components/SearchInput.vue'

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
  searchSize: {
    type: Number,
    required: true,
  },
})

const {
  hasAccess,
  hasRole,
  isProfileVisibility,
  isProfileStatus,
  accessControl,
  isSubscriptionGroup,
} = usePermissionSystem()
const { openModal } = useModals()
const { screenWidth, tailwindScreens } = useScreenWidth()
const { currentUser } = useCurrentUser()

const isFiltersSectionShown = ref(false)
const totalMentees = ref(0)
const nextOffset = ref(0)
const mentees = ref([])
const isLoading = ref(false)
const isError = ref(false)
const lightPlanAccessDeniedModalName = 'light-plan-access-denied-modal'
const mappedFilters = ref({
  looking_fors: props.filters.lookingFors,
  roles: props.filters.roles,
  industry_specialties: props.filters.industrySpecialties,
  languages: props.filters.languages,
  skills: props.filters.skills,
  challenges: props.filters.challenges,
  interests: props.filters.interests,
  living_in_country: props.filters.countries.map((country) => ({
    ...country,
    icon: `https://flagcdn.com/w20/${country.value.toLowerCase()}.png`,
  })),
})

const {
  filtersState,
  isAnyFilterActive,
  clearFilters,
  loadPreselectedFilters,
  activeFilters,
  activeFiltersCount,
} = useFiltersProvider({
  initialState: {
    looking_fors: { value: [], option: 'must' },
    skills: { value: [], option: 'must' },
    challenges: { value: [], option: 'must' },
    interests: { value: [], option: 'must' },
    roles: { value: [], option: 'must' },
    industry_specialties: { value: [], option: 'must' },
    living_in_country: { value: [], option: 'must' },
    languages: { value: [], option: 'must' },
    text: { value: null, option: 'must' },
  },
  tracking: {
    challenges: {
      name: 'Community Filter Clicked Challenges',
      valueKey: 'challenge_filter',
    },
    looking_fors: {
      name: 'Community Filter Clicked Networking Objective',
      valueKey: 'networking_objective_filter',
    },
    roles: {
      name: 'Community Filter Clicked Role',
      valueKey: 'role_filter',
    },
    skills: {
      name: 'Community Filter Clicked Skills',
      valueKey: 'skill_filter',
    },
    languages: {
      name: 'Community Filter Clicked Languages',
      valueKey: 'language_filter',
    },
    living_in_country: {
      name: 'Community Filter Clicked Country',
      valueKey: 'country_filter',
    },
    interests: {
      name: 'Community Filter Clicked Interests',
      valueKey: 'interest_filter',
    },
    industry_specialties: {
      name: 'Community Filter Clicked Industries',
      valueKey: 'industry_filter',
    },
  },
  availableFilters: mappedFilters,
})

const hasAccessToFilteringCommunityMembers = computed(() => {
  return accessControl.value.subscription.privileges.filterCommunityMembers
})

const isCommunityPageBlocked = computed(() => {
  return (
    hasAccess(ACCESS_TYPE.NONE) ||
    isProfileStatus(PROFILE_STATUS.NOT_COMPLETE) ||
    isProfileVisibility(PROFILE_VISIBILITY.INVISIBLE) ||
    !hasAccessToFilteringCommunityMembers.value
  )
})

const canLoadMoreMentees = computed(() => {
  return nextOffset.value <= totalMentees.value
})

const searchInput = computed({
  get() {
    return filtersState.value.text.value
  },
  set(newValue) {
    if (isCommunityPageBlocked.value) return
    filtersState.value.text.value = newValue
    applySearch()
  },
})

const applySearch = _debounce(function () {
  reloadMentees()
  if (searchInput.value) {
    window.segmentAnalytics.track('Search Browse Community', {
      user_type: window.segmentAnalytics.userType(),
      search_input: searchInput.value.toLowerCase(),
    })
  }
}, 1000)

const applyFilters = _debounce(function () {
  reloadMentees()
  fetchFilters()
}, 500)

const loadMentees = async () => {
  if (!canLoadMoreMentees.value) return

  isLoading.value = true
  try {
    const { data } = await axios.patch('/mentees/search', {
      ...filtersState.value,
      sort: {
        field: '_score',
        order: 'desc',
      },
      size: props.searchSize,
      offset: nextOffset.value,
    })
    mentees.value = mentees.value.concat(data.results)
    totalMentees.value = data.total
    nextOffset.value += props.searchSize
    isLoading.value = false
  } catch (_) {
    isError.value = true
  }
}

const fetchFilters = async () => {
  const params = queryParameters().getParams()
  const { data } = await axios.get('mentees/filters', {
    params: { filters: params },
  })
  mappedFilters.value.looking_fors = data.lookingFors
  mappedFilters.value.roles = data.roles
  mappedFilters.value.industry_specialties = data.industrySpecialties
  mappedFilters.value.languages = data.languages
  mappedFilters.value.skills = data.skills
  mappedFilters.value.challenges = data.challenges
  mappedFilters.value.interests = data.interests
  mappedFilters.value.living_in_country = data.countries
}

const resetLastSearchData = () => {
  mentees.value = []
  nextOffset.value = 0
}

const reloadMentees = () => {
  queryParameters().setParams(activeFilters.value)
  resetLastSearchData()
  loadMentees()
}

const handleFiltersClick = async () => {
  if (hasRole(ROLE.GUEST)) {
    return openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  } else if (hasAccess(ACCESS_TYPE.NONE)) {
    return router.visit('/upgrade_plan/new')
  } else if (isProfileStatus(PROFILE_STATUS.NOT_COMPLETE)) {
    return router.visit(currentUser.value.profileUrl)
  } else if (isProfileVisibility(PROFILE_VISIBILITY.INVISIBLE)) {
    return router.visit('/settings/profile/basic')
  } else if (!hasAccessToFilteringCommunityMembers.value) {
    return openModal(lightPlanAccessDeniedModalName)
  }
}

const handleClearFiltersButtonClick = () => {
  clearFilters()
  applyFilters()
}

const toggleFiltersSection = () => {
  isFiltersSectionShown.value = !isFiltersSectionShown.value
}

onBeforeMount(() => {
  // Filters initialization
  const params = queryParameters().getParams()
  loadPreselectedFilters(params)

  loadMentees()
})

onBeforeUnmount(() => {
  clearFilters()
})
</script>
